import React, { useState } from 'react'
import { IconButton, Drawer, List, ListItemButton, ListItemText, Box, BoxProps, MenuItem } from '@mui/material'
import { Menu as MenuIcon } from '@mui/icons-material'
import withCapabilities from '../../../hoc/withCapabilities'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { MenuItem as MenuItemType } from '../../../types/menuItem.types'
import { AvatarIcon, LogoutIcon } from '../../styles/Icons.styled'
import { BodyText } from '../../styles/Typography.styled'
import { useAtom } from 'jotai'
import { userAtom } from '../../../state/userAtom'
import { theme } from '../../../theme'
import { HorizontalMenuSeparator } from './SideBarMenu.styled'

interface MobileMenuProps extends BoxProps {
  menuItems: MenuItemType[]
  openFrom: 'left' | 'right'
}

const SideBarMenu: React.FC<MobileMenuProps> = ({ menuItems, openFrom, ...props }) => {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { logout } = useAuth()
  const { t } = useTranslation()
  const [user] = useAtom(userAtom)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  return (
    <Box {...props}>
      <IconButton aria-label={t('common.navigation')} color="inherit" edge="start" onClick={handleDrawerToggle}>
        <MenuIcon />
      </IconButton>

      {/* ----------------------------- Mobile Drawer ----------------------------- */}
      <Drawer
        variant="temporary"
        anchor={openFrom}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { mobile: 'block', laptop: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        <List>
          {/* ----------------------------- Avatar & Username ----------------------------- */}
          <ListItemButton key="avatar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, mb: 5 }}>
            <AvatarIcon customColor={theme.palette.secondary.main} />
            <BodyText color="secondary.main" sx={{ marginLeft: 1 }}>
              {user?.firstName} {user?.lastName}
            </BodyText>
          </ListItemButton>
          {/* ----------------------------- Menu Items ----------------------------- */}
          {menuItems.map((item) => (
            <React.Fragment key={item.url}>
              <ListItemButton
                component={Link}
                to={item.url}
                selected={location.pathname === item.url || location.pathname.startsWith(item.url + '/')}
                onClick={handleDrawerToggle}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                }}
              >
                {/* ----------------------------- Icon ----------------------------- */}
                {React.cloneElement(item.icon, {
                  customColor: 'text.primary',
                  size: '25px',
                })}
                {/* ----------------------------- Label ----------------------------- */}
                <ListItemText primary={item.label} />
              </ListItemButton>
              <HorizontalMenuSeparator />
            </React.Fragment>
          ))}
        </List>
        {/* ----------------------------- Logout ----------------------------- */}
        <MenuItem
          sx={{ position: 'absolute', bottom: 30, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}
          onClick={logout}
        >
          <LogoutIcon customColor="text.primary" size="25px" />
          {t('header.logout')}
        </MenuItem>
      </Drawer>
    </Box>
  )
}

export default withCapabilities(SideBarMenu)
